import { i18n } from '@/locale/i18n'
import { getSportsEventData } from '@/features/market/utils/sportsEvent'
import { getSportOutcomeLabel, resolveSportOutcomeSlots } from '@/features/market/utils/sportOutcomeSlots'

/**
 * Gets the labels for position buttons (long_label and short_label)
 * Falls back to Yes/No/Up/Down if labels are not available
 * 
 * @param {Object} outcome - The outcome object (can be null/undefined)
 * @param {Object} question - The question/market object
 * @returns {{longLabel: string, shortLabel: string}}
 */
export const getPositionLabels = (outcome, question) => {
  // Use long_label and short_label from outcome if available
  if (outcome?.long_label && outcome?.short_label) {
    return {
      longLabel: outcome.long_label,
      shortLabel: outcome.short_label,
    }
  }

  // Fallback: use first outcome's labels for binary markets
  if (question?.outcomes?.[0]?.long_label && question?.outcomes?.[0]?.short_label) {
    return {
      longLabel: question.outcomes[0].long_label,
      shortLabel: question.outcomes[0].short_label,
    }
  }

  // Fallback: use i18n based on position label style (not outcomes_type)
  const isUpDown =
    outcome?.position_labels === 'updown' ||
    question?.outcomes?.[0]?.position_labels === 'updown'
  return {
    longLabel: isUpDown ? i18n('place_bet.up') : i18n('place_bet.yes'),
    shortLabel: isUpDown ? i18n('place_bet.down') : i18n('place_bet.no'),
  }
}

/**
 * Chart display label for an outcome series:
 *  - Mono markets (single backend outcome): use position long/short labels (e.g. Yes/No, Up/Down, custom).
 *  - Multi-outcome markets: use the outcome's title (soccer matches use the team short code).
 *  - Falls back to the series.name if no outcome data is available.
 *
 * @param {Object} question - The question/market object
 * @param {Object} series - The chart series (with a `name` field used as fallback)
 * @param {number} index - Index of this series within the chart
 * @param {number} seriesCount - Total number of visible series in the chart
 */
export const getOutcomeDisplayLabel = (question, series, index, seriesCount) => {
  const outcomes = question?.outcomes
  const outcomesLen = outcomes?.length ?? 0
  if (outcomesLen === 1 && outcomes[0]) {
    const positionLabels = getPositionLabels(outcomes[0], question)
    if (seriesCount > 1) {
      return index === 0 ? positionLabels.longLabel : positionLabels.shortLabel
    }
    return positionLabels.longLabel
  }
  if (outcomesLen > 1) {
    const outcome = outcomes[index]
    if (!outcome) return series?.name

    // Soccer matches: short codes (ALG, AUT) instead of full team names.
    const sportsData = getSportsEventData(question)
    if (sportsData) {
      return getSportOutcomeLabel(outcome, resolveSportOutcomeSlots(outcomes, sportsData), sportsData)
    }

    return outcome.title ?? series?.name
  }
  return series?.name
}

